/**
 * Right rail for the family weekly order view: read-only month calendar
 * (selected week banded, days with submitted orders green) → cart. Mirrors the
 * school `RightRail`. Week-closed state comes from the location's deadline
 * policy via `weekDeadlineFor` (FIXED_WEEKLY: the weekly cutoff;
 * RELATIVE_DAYS: closed only once the week's last service day has closed).
 */

import { cn } from '@ordo/ui/cn'
import type { ReactElement } from 'react'

import { WeekMonthCalendar } from '@/components/orders/school/week-month-calendar'
import { mondayOf } from '@/lib/school-orders/calendar'

import { FamilyCartPanel } from './cart-panel'
import type { FamilyOrderLocation } from './columns'
import { weekClosedForAll } from './day-badges'

export interface FamilyRightRailProps {
  locations: readonly FamilyOrderLocation[]
  /** Mon–Fri keys of the selected week. */
  weekKeys: string[]
  /** Dates (`YYYY-MM-DD`) with at least one submitted order — shown green. */
  orderedDates: ReadonlySet<string>
  /** Pin the rail to this px height. `null` → fill the column. */
  height?: number | null
  onSelectWeek: (mondayKey: string) => void
  onCheckout: () => void
  submitting?: boolean
  /**
   * Render the cart panel. False when no campus in view takes parent orders
   * (school ordering only, PROD-1554): there is nothing to add to a cart, so
   * the rail keeps only the calendar.
   */
  showCart?: boolean
}

export function FamilyRightRail({
  locations,
  weekKeys,
  orderedDates,
  height,
  onSelectWeek,
  onCheckout,
  submitting,
  showCart = true,
}: FamilyRightRailProps): ReactElement {
  const weekMonday = weekKeys[0] ?? ''
  const isWeekClosed = (mondayKey: string): boolean => weekClosedForAll(locations, mondayOf(mondayKey))
  return (
    <aside
      className={cn('flex min-h-0 flex-col gap-4 lg:max-h-full', height == null && 'flex-1')}
      style={height == null ? undefined : { height }}>
      <div className="shrink-0">
        <WeekMonthCalendar
          anchorKey={weekMonday}
          selectedWeekKeys={weekKeys}
          orderedDates={orderedDates}
          isWeekClosed={isWeekClosed}
          onSelectWeek={onSelectWeek}
        />
      </div>
      {showCart ? <FamilyCartPanel onCheckout={onCheckout} submitting={submitting} className="min-h-0" /> : null}
    </aside>
  )
}
